<template>
  <div class="lookup-container">
    <h2>Enter VIN or Job Number</h2>

    <div class="request-form">
      <div class="request-header">
        <input v-model="vin" type="text" placeholder="Enter VIN or Job Number" class="url-input"
          @keyup.enter="sendRequest" />

        <button @click="sendRequest" :disabled="loading || !vin" class="send-button">
          {{ loading ? 'Loading...' : 'Send' }}
        </button>
      </div>

    </div>

    <div v-if="error" class="error-message">
      <h3>Error</h3>
      <pre>{{ error }}</pre>
    </div>

    <!-- vin lookup section -->
    <div class="ag-grid-container">
      <ag-grid-vue 
        :rowData="rowData" 
        :columnDefs="colDefs" 
        :defaultColDef="defaultColDef" 
        :theme="myTheme"
        :loading="gridLoading"
        :overlayLoadingTemplate="overlayLoadingTemplate"
        :overlayNoRowsTemplate="overlayNoRowsTemplate"
        @grid-ready="onGridReady"
        style="height: 500px">
      </ag-grid-vue>
    </div>


    <!-- job lookup section -->
    <!-- <div class="ag-grid-container">
      <ag-grid-vue :rowData="rowData" :columnDefs="colDefs" :defaultColDef="defaultColDef" style="height: 500px">
      </ag-grid-vue>
    </div> -->

    <!-- PDF Modal -->
    <div v-if="showModal" class="modal-overlay" @click="closeModal">
      <div class="modal-content" @click.stop>
        <button class="modal-close" @click="closeModal">&times;</button>
        <iframe v-if="pdfUrl" :src="pdfUrl" class="pdf-viewer"></iframe>
      </div>
    </div>

    <!-- Drawing Request Modal -->
    <div v-if="showRequestModal" class="modal-overlay" @click="closeRequestModal">
      <div class="modal-content-form" @click.stop>
        <button class="modal-close" @click="closeRequestModal">&times;</button>
        
        <!-- Success Message -->
        <div v-if="requestSubmitted" class="success-message">
          <h3>Request Submitted!</h3>
          <p>Your drawing request for <strong>{{ requestJobItem }}</strong> has been submitted successfully.</p>
          <p>You will be notified when the drawing becomes available.</p>
          <div class="form-actions">
            <button 
              type="button" 
              @click="closeRequestModal" 
              class="btn-submit"
            >
              Ok
            </button>
          </div>
        </div>
        
        <!-- Request Form -->
        <div v-else>
          <h3>Drawing Not Available</h3>
          <p>The drawing for <strong>{{ requestJobItem }}</strong> is not currently available.</p>
          <p>Please provide your information to request this drawing:</p>
          
          <form @submit.prevent="submitDrawingRequest" class="request-form-content">
            <div class="form-group">
              <label for="name">Name:</label>
              <input 
                type="text" 
                id="name" 
                v-model="requestForm.name" 
                placeholder="Enter your name"
                required
                class="form-input"
              />
            </div>
            
            <div class="form-group">
              <label for="email">Email:</label>
              <input 
                type="email" 
                id="email" 
                v-model="requestForm.email" 
                placeholder="Enter your email"
                required
                class="form-input"
              />
            </div>
            
            <div class="form-actions">
              <button 
                type="button" 
                @click="closeRequestModal" 
                class="btn-cancel"
                :disabled="submittingRequest"
              >
                Cancel
              </button>
              <button 
                type="submit" 
                class="btn-submit"
                :disabled="submittingRequest"
              >
                {{ submittingRequest ? 'Submitting...' : 'Submit Request' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>

</template>

<script>
import { ref, watch } from 'vue';
import { AgGridVue } from 'ag-grid-vue3';
import { themeQuartz } from 'ag-grid-community';

// Custom AG Grid theme configuration
const myTheme = themeQuartz
  .withParams({
    accentColor: "#087AD1",
    backgroundColor: "#FFFFFF",
    borderColor: "#D7E2E6",
    borderRadius: 2,
    browserColorScheme: "light",
    cellHorizontalPaddingScale: 0.7,
    chromeBackgroundColor: {
      ref: "backgroundColor"
    },
    columnBorder: false,
    fontFamily: {
      googleFont: "Avenir, Helvetica, Arial, sans-serif"
    },
    fontSize: 13,
    foregroundColor: "#555B62",
    headerBackgroundColor: "#FFFFFF",
    headerFontSize: 13,
    headerFontWeight: 400,
    headerTextColor: "#181D1F",
    rowBorder: true,
    rowVerticalPaddingScale: 0.8,
    sidePanelBorder: true,
    spacing: 2,
    wrapperBorder: false,
    wrapperBorderRadius: 2
  });

export default {
  name: 'VinLookup',
  components: {
    AgGridVue,
  },
  setup() {
    const url = ref(process.env.VUE_APP_API_BASE_URL);
    const vin = ref('');
    const error = ref(null);
    const loading = ref(false);
    const gridLoading = ref(false);
    const showModal = ref(false);
    const pdfUrl = ref(null);
    const loadingDrawing = ref(null);
    const gridApi = ref(null);
    
    // Drawing request modal state
    const showRequestModal = ref(false);
    const requestJobItem = ref(null);
    const requestForm = ref({
      name: '',
      email: ''
    });
    const submittingRequest = ref(false);
    const requestSubmitted = ref(false);

    const sendRequest = async () => {
      if (!url.value) return;

      loading.value = true;
      gridLoading.value = true;
      error.value = null;
      
      // Clear the grid before loading
      rowData.value = [];

      try {
        const options = {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        };

        const fullUrl = `${url.value}/lookup/${vin.value}`;

        const res = await fetch(fullUrl, options);
        const data = await res.json().catch(() => res.text());

        rowData.value = data;
      } catch (err) {
        error.value = err.message || 'Failed to make request';
      } finally {
        loading.value = false;
        gridLoading.value = false;
      }
    };

    // Ag Grid 

    const rowData = ref([]);

    // Callback function for View Drawing button
    const onViewDrawing = async (params) => {
      console.log('View Drawing clicked for:', params.data);
      const jobItem = params.data.JobItem;
      const rowIndex = params.node.rowIndex;
      
      try {
        // Set loading state for this specific button using row index
        loadingDrawing.value = rowIndex;
        
        const res = await fetch(`${url.value}/drawing/${jobItem}`, {
          method: 'GET',
        });

        // Check if drawing doesn't exist (500 error)
        if (res.status === 500) {
          // Open request modal
          requestJobItem.value = jobItem;
          showRequestModal.value = true;
          return;
        }

        if (!res.ok) {
          throw new Error(`Failed to fetch drawing: ${res.statusText}`);
        }

        // Get the PDF as a blob
        const blob = await res.blob();
        
        // Create a URL for the blob
        const blobUrl = URL.createObjectURL(blob);
        
        // Open the PDF in modal
        pdfUrl.value = blobUrl;
        showModal.value = true;
      } catch (err) {
        console.error('Error viewing drawing:', err);
        error.value = err.message || 'Failed to load drawing';
      } finally {
        // Clear loading state
        loadingDrawing.value = null;
      }
    };

    // Close modal function
    const closeModal = () => {
      showModal.value = false;
      // Clean up the blob URL when closing modal
      if (pdfUrl.value) {
        URL.revokeObjectURL(pdfUrl.value);
        pdfUrl.value = null;
      }
    };

    // Close request modal function
    const closeRequestModal = () => {
      showRequestModal.value = false;
      requestJobItem.value = null;
      requestForm.value = {
        name: '',
        email: ''
      };
      requestSubmitted.value = false;
    };

    // Submit drawing request
    const submitDrawingRequest = async () => {
      if (!requestForm.value.name || !requestForm.value.email) {
        error.value = 'Please fill in both name and email';
        return;
      }

      try {
        submittingRequest.value = true;
        error.value = null;

        const res = await fetch(`${url.value}/drawing/request`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            jobItem: requestJobItem.value,
            name: requestForm.value.name,
            email: requestForm.value.email
          })
        });

        if (!res.ok) {
          throw new Error(`Failed to submit request: ${res.statusText}`);
        }

        // Success - show success message in modal
        requestSubmitted.value = true;
      } catch (err) {
        console.error('Error submitting drawing request:', err);
        error.value = err.message || 'Failed to submit drawing request';
      } finally {
        submittingRequest.value = false;
      }
    };

    // Column Definitions: Defines the columns to be displayed.
    const colDefs = ref([
      { 
        field: "Drawing", 
        headerName: "Drawing Retrieval",
        cellRenderer: (params) => {
          const button = document.createElement('button');
          const isLoading = loadingDrawing.value === params.node.rowIndex;
          button.innerText = isLoading ? 'Loading...' : 'View Drawing';
          button.className = 'view-drawing-btn';
          button.disabled = isLoading;
          button.addEventListener('click', () => onViewDrawing(params));
          return button;
        }
      },
      { field: "JobItem", headerName: "Item" },
      { field: "JobItemDescription", headerName: "Description" },
      { field: "QtyReleased", headerName: "Qty Per" },
      { field: "Revision", headerName: "Revision" }
    ]);

    // Default Column Definition: Enables filtering and floating filters for all columns
    const defaultColDef = ref({
      filter: true,
      floatingFilter: true,
      sortable: true,
      resizable: true
    });

    // Custom Loading Overlay Template (HTML string)
    const overlayLoadingTemplate = `
      <div class="ag-overlay-loading-center" style="background-color: white; padding: 20px;">
        <div class="spinner"></div>
        <div style="margin-top: 10px; color: #555B62;">Loading data...</div>
      </div>
    `;

    // Custom No Rows Overlay Template (HTML string)
    const overlayNoRowsTemplate = `
      <div class="ag-overlay-no-rows-center" style="background-color: white;">
        <div style="padding: 20px; text-align: center; color: #555B62;">
          No rows to show
        </div>
      </div>
    `;

    // Grid ready callback to capture API reference
    const onGridReady = (params) => {
      gridApi.value = params.api;
    };

    // Watch for loading state changes and refresh the Drawing column cells
    watch(loadingDrawing, () => {
      if (gridApi.value) {
        gridApi.value.refreshCells({ columns: ['Drawing'], force: true });
      }
    });

    return {
      url,
      vin,
      error,
      loading,
      gridLoading,
      sendRequest,
      rowData,
      colDefs,
      defaultColDef,
      myTheme,
      overlayLoadingTemplate,
      overlayNoRowsTemplate,
      showModal,
      pdfUrl,
      closeModal,
      onGridReady,
      showRequestModal,
      requestJobItem,
      requestForm,
      submittingRequest,
      requestSubmitted,
      closeRequestModal,
      submitDrawingRequest,
    };
  },
};
</script>

<style>
.lookup-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

h2 {
  color: #2c3e50;
  margin-bottom: 20px;
}

.request-form {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
}

.request-header {
  display: flex;
  gap: 10px;
  margin-bottom: 15px;
}

.url-input {
  flex: 1;
  padding: 5px 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.url-input:focus {
  outline: none;
  border-color: #42b983;
}

.send-button {
  padding: 10px 30px;
  background: #42b983;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.3s;
}

.send-button:hover:not(:disabled) {
  background: #35a372;
}

.send-button:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.view-drawing-btn {
  padding: 3px 10px;
  background: #42b983;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.3s;
  line-height: 1.2;
  min-width: 90px;
  text-align: center;
}

.view-drawing-btn:hover:not(:disabled) {
  background: #35a372;
}

.view-drawing-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
  opacity: 0.6;
}

/* Spinner animation for loading overlay */
.spinner {
  border: 4px solid #f3f3f3;
  border-top: 4px solid #42b983;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 0 auto;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Modal styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.modal-content {
  position: relative;
  background: white;
  border-radius: 8px;
  width: 90%;
  height: 90%;
  max-width: 1200px;
  max-height: 900px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
}

.modal-close {
  position: absolute;
  top: 10px;
  right: 10px;
  background: #42b983;
  color: white;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  font-size: 24px;
  cursor: pointer;
  z-index: 1001;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.3s;
}

.modal-close:hover {
  background: #35a372;
}

.pdf-viewer {
  width: 100%;
  height: 100%;
  border: none;
  border-radius: 8px;
}

/* Request Form Modal */
.modal-content-form {
  position: relative;
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 500px;
  padding: 30px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.modal-content-form h3 {
  margin-top: 0;
  margin-bottom: 15px;
  color: #2c3e50;
  font-size: 20px;
}

.modal-content-form p {
  margin-bottom: 10px;
  color: #555B62;
  line-height: 1.5;
}

.modal-content-form p strong {
  color: #2c3e50;
}

.success-message {
  text-align: center;
}

.success-message h3 {
  color: #42b983;
  font-size: 24px;
  margin-bottom: 20px;
}

.success-message p {
  font-size: 15px;
}

.success-message .form-actions {
  justify-content: center;
  margin-top: 30px;
}

.success-message .btn-submit {
  min-width: 120px;
}

.request-form-content {
  margin-top: 20px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  color: #2c3e50;
  font-weight: 500;
  font-size: 14px;
}

.form-input {
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  box-sizing: border-box;
}

.form-input:focus {
  outline: none;
  border-color: #42b983;
  box-shadow: 0 0 0 2px rgba(66, 185, 131, 0.1);
}

.form-actions {
  display: flex;
  gap: 10px;
  margin-top: 25px;
  justify-content: flex-end;
}

.btn-cancel,
.btn-submit {
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.3s;
}

.btn-cancel {
  background: #f8f9fa;
  color: #555B62;
  border: 1px solid #ddd;
}

.btn-cancel:hover:not(:disabled) {
  background: #e9ecef;
}

.btn-submit {
  background: #42b983;
  color: white;
}

.btn-submit:hover:not(:disabled) {
  background: #35a372;
}

.btn-cancel:disabled,
.btn-submit:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
